import { hasClassroomJourneyProgress, hasCompletedClassroomJourneys, hasStartedSameGroupings } from "./classroomHandoverProgress";
import { formatAcademicYear } from "./academicYear";

const getClassAcademicYear = (classroom) =>
  classroom.academicYear || classroom.academic_year || classroom.academicyear || classroom.year;

const getClassProgressionKey = (classroom) =>
  classroom.progressionKey || classroom.progression_key;

export const getGradeLevelFromProgressionKey = (progressionKey) => {
  const match = progressionKey?.match(/^grade(\d+)$/);
  return match ? Number(match[1]) : null;
};

export const getProgressionTargetLabel = (label) => {
  if (typeof label !== "string") return null;
  return label.split("→").pop().trim() || null;
};

export const destinationWithSelection = (destination = {}) => {
  const url = destination.url || "/classadmindashboard";
  const [path, search = ""] = url.split("?");
  const params = new URLSearchParams(search);
  if (destination.academicYear) params.set("academicYear", destination.academicYear);
  if (destination.progressionKey) params.set("progressionKey", destination.progressionKey);
  const query = params.toString();
  return query ? `${path}?${query}` : path;
};

const academicYearsMatch = (first, second) => {
  try {
    return formatAcademicYear(first, "storage") ===
      formatAcademicYear(second, "storage");
  } catch (_) {
    return false;
  }
};

export const classroomMatchesHandoverSelection = (classroom, selection) => {
  if (!selection) return false;

  const academicYear = getClassAcademicYear(classroom);
  const progressionKey = getClassProgressionKey(classroom);
  const gradeLevel = selection.gradeLevel ??
    getGradeLevelFromProgressionKey(selection.progressionKey);
  const progressionMatches = progressionKey
    ? progressionKey === selection.progressionKey
    : gradeLevel !== null && classroom[`grade_level${gradeLevel}`] === true;

  return academicYearsMatch(academicYear, selection.academicYear) && progressionMatches;
};

export const parseClassroomHandoverSelection = (search = "") => {
  const params = new URLSearchParams(search);
  const academicYear = params.get("academicYear");
  const progressionKey = params.get("progressionKey");

  return academicYear && progressionKey ? { academicYear, progressionKey } : null;
};

export const getClassroomHandoverDashboardDestination = (search = "") => {
  const selection = parseClassroomHandoverSelection(search);
  return selection
    ? destinationWithSelection({ url: "/classadmindashboard", ...selection })
    : "/classadmindashboard";
};

export const validateClassroomHandoverSelection = (entry, requestedSelection) => {
  if (!entry || !requestedSelection) return null;
  if (requestedSelection.academicYear !== entry.academicYear) return null;

  const progressionPoint = (entry.progressionPoints || []).find((point) =>
    point.enabled === true && !point.journeyConflict &&
    (
      hasStartedSameGroupings(point) ||
      (!Object.prototype.hasOwnProperty.call(point, "groupingDecision") &&
        entry.groupingDecision === "same_groups")
    ) &&
    point.key === requestedSelection.progressionKey &&
    (point.destination?.academicYear ?? entry.academicYear) === requestedSelection.academicYear &&
    (point.destination?.progressionKey ?? point.key) === requestedSelection.progressionKey
  );

  if (!progressionPoint) return null;

  return {
    ...requestedSelection,
    gradeLevel: getGradeLevelFromProgressionKey(
      requestedSelection.progressionKey
    ),
    progressionPoint,
  };
};

export const canLoadLegacyClaimables = (entryStatus) =>
  entryStatus === "not_found" ||
  entryStatus === "not_required";

const findSelectedProgressionPoint = (task, progressionKey) => {
  if (!task) return null;
  const selectedIds = new Set(task.selectedProgressionPointIds);
  for (const point of task.progressionPoints) {
    if (!selectedIds.has(point.id)) continue;
    const pointKey = point.progressionKey ?? point.id;
    if (pointKey === progressionKey) return point;
  }
  return null;
};

const countConfirmedClassrooms = (classrooms = []) => {
  let count = 0;
  classrooms.forEach((classroom) => {
    if (!classroom?.id) return;
    if (!classroom.name?.trim()) return;
    count += 1;
  });
  return count;
};

const hasCompletedClassroomCount = ({
  classroomProgress = {},
  classroomCount,
  confirmedClassrooms,
}) => {
  const expected = classroomProgress.expected ?? classroomCount;
  if (!Number.isSafeInteger(expected)) return false;
  if (expected <= 0) return false;

  const recordedComplete = Number(classroomProgress.complete);
  const complete = Math.max(
    recordedComplete > 0 ? recordedComplete : 0,
    countConfirmedClassrooms(confirmedClassrooms)
  );
  return complete >= expected;
};

const hasAvailableDifferentGroupingsClasses = (task, requestedSelection, point) => {
  if (!task) return false;
  if (!requestedSelection) return false;
  if (task.academicYear !== requestedSelection.academicYear) return false;
  if (!point) return false;
  // A finished classroom is accessible even while the rest of its year group
  // is still being set up. Counts alone do not identify an available class.
  if (countConfirmedClassrooms(point.confirmedClassrooms) > 0) return true;
  if (point.progress === "released") return true;
  return hasCompletedClassroomCount(point);
};

const ENTRY_STATUSES = [
  "ready",
  "not_required",
  "saving",
  "stale",
  "mutation_error",
  "mutation_forbidden",
  "mutation_not_found",
];

export const getClassroomHandoverDashboardGate = ({
  entry,
  entryStatus,
  requestedSelection,
  legacyProgressionKey,
  legacyViewRequested = false,
  differentGroupingsTask,
  differentGroupingsTaskStatus = "ready",
}) => {
  const setupComplete = ["ready", "not_required"].includes(entryStatus) &&
    differentGroupingsTaskStatus === "ready" && hasCompletedClassroomJourneys(entry, differentGroupingsTask);
  const isNormalProgression = Boolean(
    requestedSelection &&
    requestedSelection.academicYear === entry?.academicYear &&
    legacyProgressionKey &&
    requestedSelection.progressionKey === legacyProgressionKey
  );
  const selectedProgressionPoint = legacyViewRequested
    ? findSelectedProgressionPoint(differentGroupingsTask, requestedSelection?.progressionKey)
    : null;
  const hasAvailableProgressionClasses = Boolean(
    legacyViewRequested &&
    hasAvailableDifferentGroupingsClasses(
      differentGroupingsTask,
      requestedSelection,
      selectedProgressionPoint
    )
  );
  const availableProgressionPoint = hasAvailableProgressionClasses
    ? selectedProgressionPoint
    : null;
  const completedSelection = setupComplete && requestedSelection?.academicYear === entry.academicYear &&
    entry.progressionPoints?.some((point) => point.key === requestedSelection?.progressionKey);
  const legacySelection = requestedSelection && (isNormalProgression || hasAvailableProgressionClasses || completedSelection)
    ? {
      ...requestedSelection,
      gradeLevel: getGradeLevelFromProgressionKey(requestedSelection.progressionKey),
      ...(availableProgressionPoint ? { progressionPoint: availableProgressionPoint } : {}),
    }
    : null;
  const isReadFailure = ["read_error", "read_forbidden"].includes(entryStatus);
  const validatedSelection = legacySelection || isReadFailure || setupComplete
    ? null
    : validateClassroomHandoverSelection(entry, requestedSelection);
  const showEntry = Boolean(
    !legacySelection &&
    !validatedSelection &&
    !setupComplete &&
    (entry?.setupRequired || hasClassroomJourneyProgress(entry, differentGroupingsTask)) &&
    ENTRY_STATUSES.includes(entryStatus)
  );

  return {
    legacySelection,
    validatedSelection,
    showEntry,
    showReadAlert: isReadFailure,
    loadLegacyClaimables: canLoadLegacyClaimables(
      entryStatus,
      validatedSelection
    ),
    useHandoverShell: Boolean(
      showEntry ||
      validatedSelection ||
      (requestedSelection && entryStatus === "loading")
    ),
  };
};

export const filterClaimablesBySelection = (claimables, validatedSelection) => {
  if (!validatedSelection) return claimables;

  return claimables.filter((classroom) =>
    classroomMatchesHandoverSelection(classroom, validatedSelection)
  );
};
